import { useState, useEffect, useRef } from 'react';
import { SearchResult } from '../services/poetryService';
import { WorkerResponse } from '../workers/search.worker';

const EMPTY_RESULT: SearchResult = { catalogs: [], poets: [], poems: [] };

/**
 * Runs the search in a Web Worker so the main thread (and the input) stays
 * fully responsive while iterating over thousands of poems.
 *
 * @param query - The (already-debounced) search string coming from the URL.
 */
export function useSearch(query: string): { results: SearchResult; isSearching: boolean } {
    const [results, setResults] = useState<SearchResult>(EMPTY_RESULT);
    const [isSearching, setIsSearching] = useState(false);
    const workerRef = useRef<Worker | null>(null);
    // Track the latest query so we can discard stale worker responses
    const latestQueryRef = useRef<string>('');

    // Create the worker once
    useEffect(() => {
        const worker = new Worker(
            new URL('../workers/search.worker.ts', import.meta.url)
        );

        worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
            const { query: respondedQuery, results: workerResults } = e.data;
            // Only apply if this response matches the most-recently requested query
            if (respondedQuery === latestQueryRef.current) {
                setResults(workerResults);
                setIsSearching(false);
            }
        };

        workerRef.current = worker;

        return () => {
            worker.terminate();
            workerRef.current = null;
        };
    }, []);

    // Post a new search task whenever the query changes
    useEffect(() => {
        latestQueryRef.current = query;

        if (!query || query.trim().length < 2) {
            setResults(EMPTY_RESULT);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        workerRef.current?.postMessage({ query });
    }, [query]);

    return { results, isSearching };
}

